import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Empty, Input, Modal, Popover, Spin, Tooltip } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { FaAddressCard } from "react-icons/fa6";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import { hasRole } from "../../../global/hooks/getUserRole";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import {
  getOptionLabel,
  itemOptions as staticItemOptions,
  resolveItemValue,
  termOptions,
} from "../../../utils/Common";
import { LicenseCodeForm } from "../../LicenseCode/Components/LicenseCodeForm";
import { buildPurchaseItemPayload } from "./purchaseLicenseItemUtils";

const hasValue = (value) => value != null && value !== "";

const getCodeGroupName = (code) =>
  String(code?.CodeName || code?.Code || `Code ${code?.CodeNo}`);

const formatPrice = (value) => `$${(Number(value) || 0).toFixed(2)}`;

const formatTerm = (term) =>
  getOptionLabel(termOptions, Number(term)) || (hasValue(term) ? term : "");

const SelectorPanel = ({ title, extra, loading, empty, children }) => (
  <div className="flex flex-col min-w-0 h-[calc(100vh-345px)] min-h-[335px] max-h-[515px] border border-[#e9e9e9] rounded-[4px] overflow-hidden">
    <div className="relative flex items-center justify-center px-3 py-[9px] bg-[color-mix(in_srgb,var(--text-color)_15%,white)] text-black text-[14px] font-medium font-['Poppins']">
      <span>{title}</span>
      {extra ? <div className="absolute right-2">{extra}</div> : null}
    </div>
    <div className="flex-1 min-h-0 overflow-y-auto">
      {loading ? (
        <div className="flex items-center justify-center h-full">
          <Spin size="small" />
        </div>
      ) : empty ? (
        <div className="flex items-center justify-center h-full">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
        </div>
      ) : (
        children
      )}
    </div>
  </div>
);

const SelectorOption = ({ active, disabledReason, onSelect, children }) => {
  const disabled = Boolean(disabledReason);
  return (
    <Tooltip title={disabledReason || ""} placement="right">
      <div
        role="option"
        aria-selected={active}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={disabled ? undefined : onSelect}
        onKeyDown={(e) => {
          if (disabled || (e.key !== "Enter" && e.key !== " ")) return;
          e.preventDefault();
          onSelect();
        }}
        className={`flex items-center justify-between gap-2 px-[9px] py-[9px] text-[14px] leading-[18px] transition-colors outline-none odd:bg-white even:bg-[#f5f5f5] ${
          disabled
            ? "!text-gray-300 cursor-not-allowed"
            : active
              ? "!bg-customColor_20 text-black font-medium cursor-pointer"
              : "text-[#50565e] cursor-pointer hover:!bg-[rgb(239,240,242)] focus-visible:!bg-[rgb(239,240,242)]"
        }`}
      >
        <span className="truncate">{children}</span>
        {active ? <CheckOutlined className="text-[12px] text-black" /> : null}
      </div>
    </Tooltip>
  );
};

export const PurchaseLicenseCodeSelector = ({
  purchaseId,
  existingItems = [],
  editingItem,
  onCancel,
  onSaved,
  onPick,
  allowDuplicateItems = false,
  disabledCodeNos = [],
  ownerLabel = "purchase",
  oemNo = null,
  isOemLicense = false,
}) => {
  const isEdit = editingItem?.ItemNo != null;
  const canCreateCode = hasRole(1, 2);
  const [picking, setPicking] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedCodeName, setSelectedCodeName] = useState(null);
  const [selectedCodeNo, setSelectedCodeNo] = useState(null);
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [itemSearch, setItemSearch] = useState("");
  const [itemSearchOpen, setItemSearchOpen] = useState(false);
  const pendingCodeNoRef = useRef(isEdit ? editingItem?.CodeNo : null);

  const codesUrl =
    isOemLicense && hasValue(oemNo)
      ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?limit=0&sort_by=Item&order=asc&page=1&OemNo=${oemNo}`
      : hasValue(selectedItem)
        ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?limit=0&sort_by=Item&order=asc&Item=${selectedItem}`
        : null;

  const {
    data: codesData,
    loading: codesLoading,
    refetch: codesRefetch,
  } = useGet(codesUrl);

  const optionUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/getoptions/`;
  const { data: optionData, loading: optionLoading } = useGet(optionUrl);
  const allItems = useMemo(() => {
    const apiItems = getModifiedResponse(optionData, "items");
    const known = new Set(staticItemOptions.map((opt) => Number(opt.value)));
    return [
      ...staticItemOptions,
      ...apiItems.filter((opt) => !known.has(Number(opt.value))),
    ];
  }, [optionData]);

  const items = useMemo(() => {
    if (!isOemLicense) return allItems;
    const itemsWithCodes = new Set(
      (codesData?.data ?? []).map((code) => Number(code.Item)),
    );
    return allItems.filter((opt) => itemsWithCodes.has(Number(opt.value)));
  }, [allItems, isOemLicense, codesData]);

  const filteredItems = useMemo(() => {
    const term = itemSearch.trim().toLowerCase();
    if (!term) return items;
    return items.filter((opt) =>
      String(opt.label).toLowerCase().includes(term),
    );
  }, [items, itemSearch]);
  const usedItemValues = useMemo(() => {
    const used = new Set();
    existingItems.forEach((row) => {
      if (isEdit && Number(row?.ItemNo) === Number(editingItem.ItemNo)) return;
      const value = resolveItemValue(row, items);
      if (value != null) used.add(value);
    });
    return used;
  }, [existingItems, items, isEdit, editingItem]);
  const getItemDisabledReason = (option) =>
    !allowDuplicateItems && usedItemValues.has(Number(option.value))
      ? `${option.label} is already added to this ${ownerLabel}.`
      : null;

  const getCodeDisabledReason = (code) =>
    disabledCodeNos.some((codeNo) => Number(codeNo) === Number(code?.CodeNo))
      ? "This is already in the list"
      : null;

  const getFirstSelectableCode = (group) =>
    group?.codes.find((code) => !getCodeDisabledReason(code)) ??
    group?.codes[0];

  const codeGroups = useMemo(() => {
    const groups = new Map();
    (codesData?.data ?? [])
      .filter((code) => Number(code?.Item) === Number(selectedItem))
      .forEach((code) => {
        const name = getCodeGroupName(code);
        if (!groups.has(name)) groups.set(name, []);
        groups.get(name).push(code);
      });
    return [...groups.entries()].map(([name, codes]) => ({
      name,
      codes: [...codes].sort((a, b) => Number(a.Term) - Number(b.Term)),
    }));
  }, [codesData, selectedItem]);

  const activeGroup = codeGroups.find((g) => g.name === selectedCodeName);
  const selectedCode = activeGroup?.codes.find(
    (code) => Number(code.CodeNo) === Number(selectedCodeNo),
  );

  useEffect(() => {
    if (selectedItem != null || !items.length) return;
    const editingValue = isEdit ? resolveItemValue(editingItem, items) : null;
    const firstAvailable = items.find((opt) => !getItemDisabledReason(opt));
    const next = editingValue ?? firstAvailable?.value ?? null;
    if (next != null) setSelectedItem(Number(next));
  }, [items, usedItemValues]);

  useEffect(() => {
    if (!codeGroups.length) return;
    const pending = pendingCodeNoRef.current;
    if (pending != null) {
      const group = codeGroups.find((g) =>
        g.codes.some((code) => Number(code.CodeNo) === Number(pending)),
      );
      if (group) {
        pendingCodeNoRef.current = null;
        setSelectedCodeName(group.name);
        setSelectedCodeNo(Number(pending));
        return;
      }
    }
    if (activeGroup) {
      if (!selectedCode) {
        setSelectedCodeNo(getFirstSelectableCode(activeGroup)?.CodeNo);
      }
      return;
    }
    setSelectedCodeName(codeGroups[0].name);
    setSelectedCodeNo(getFirstSelectableCode(codeGroups[0])?.CodeNo ?? null);
  }, [codeGroups]);

  const postUrl = `${process.env.REACT_APP_API_URL}/api/purchases/${purchaseId}/items/`;
  const putUrl = `${process.env.REACT_APP_API_URL}/api/purchases/${purchaseId}/items/${editingItem?.ItemNo}/`;
  const { loading: postLoading, error: postError, postData } = usePost(postUrl);
  const { loading: putLoading, error: putError, putData } = usePut(putUrl);
  const saving = postLoading || putLoading || picking;

  useEffect(() => {
    const error = postError || putError;
    if (error) {
      openErrorNotification(
        error?.response?.data?.message || "Something went wrong",
      );
    }
  }, [postError, putError]);

  const onSelectItem = (value) => {
    if (Number(value) === Number(selectedItem)) return;
    setSelectedItem(Number(value));
    setSelectedCodeName(null);
    setSelectedCodeNo(null);
  };

  const onSelectCodeGroup = (group) => {
    setSelectedCodeName(group.name);
    setSelectedCodeNo(getFirstSelectableCode(group)?.CodeNo ?? null);
  };

  const handleCodeCreated = (created) => {
    setCodeModalOpen(false);
    if (created?.CodeNo == null) {
      codesRefetch?.();
      return;
    }
    const createdItem = hasValue(created?.Item) ? Number(created.Item) : null;
    if (
      createdItem != null &&
      !allowDuplicateItems &&
      usedItemValues.has(createdItem)
    ) {
      codesRefetch?.();
      return;
    }
    pendingCodeNoRef.current = created.CodeNo;
    if (isOemLicense) codesRefetch?.();
    if (createdItem != null && createdItem !== Number(selectedItem)) {
      onSelectItem(createdItem);
    } else if (!isOemLicense) {
      codesRefetch?.();
    }
  };

  const onApply = async () => {
    if (!selectedCode || getCodeDisabledReason(selectedCode)) {
      openErrorNotification("Please select a price.");
      return;
    }
    if (onPick) {
      setPicking(true);
      const accepted = await onPick({
        ...selectedCode,
        ItemName: selectedItemLabel,
        CodeName: selectedCodeName,
      });
      setPicking(false);
      if (accepted === false) return;
      onCancel?.();
      return;
    }
    if (isEdit && Number(selectedCode.CodeNo) === Number(editingItem.CodeNo)) {
      onCancel?.();
      return;
    }
    const payload = buildPurchaseItemPayload({
      codeNo: selectedCode.CodeNo,
      term: selectedCode.Term,
      startTime: isEdit ? editingItem.StartTime : null,
      renewal: isEdit ? editingItem.Renewal : 0,
    });
    const result = isEdit ? await putData(payload) : await postData(payload);
    if (result == null) return;
    openConfirmNotification("Success");
    onSaved?.();
    onCancel?.();
  };

  const selectedItemLabel = items.find(
    (opt) => Number(opt.value) === Number(selectedItem),
  )?.label;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
        <SelectorPanel
          title="Item"
          loading={
            (optionLoading || (isOemLicense && codesLoading)) && !items.length
          }
          empty={!filteredItems.length}
          extra={
            <Popover
              trigger="click"
              placement="bottomRight"
              arrow={false}
              open={itemSearchOpen}
              onOpenChange={setItemSearchOpen}
              destroyTooltipOnHide
              content={
                <Input
                  size="small"
                  autoFocus
                  allowClear
                  placeholder="Search item"
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  onPressEnter={() => setItemSearchOpen(false)}
                  style={{ width: 200 }}
                />
              }
            >
              <Button
                size="small"
                type="text"
                aria-label="Search item"
                className={`!h-[24px] !w-[24px] !p-0 !shadow-none ${
                  itemSearch ? "!text-customColor" : "!text-black"
                }`}
              >
                <SearchOutlined />
              </Button>
            </Popover>
          }
        >
          {filteredItems.map((option) => (
            <SelectorOption
              key={option.value}
              active={Number(option.value) === Number(selectedItem)}
              disabledReason={getItemDisabledReason(option)}
              onSelect={() => onSelectItem(option.value)}
            >
              {option.label}
            </SelectorOption>
          ))}
        </SelectorPanel>

        <SelectorPanel
          title="Code"
          loading={codesLoading && !codeGroups.length}
          empty={!hasValue(selectedItem) || !codeGroups.length}
          extra={
            canCreateCode ? (
              <Tooltip title="New Code">
                <Button
                  size="small"
                  type="text"
                  aria-label="New Code"
                  className="!text-black !h-[24px] !w-[24px] !p-0 !shadow-none"
                  onClick={() => setCodeModalOpen(true)}
                >
                  <PlusOutlined />
                </Button>
              </Tooltip>
            ) : null
          }
        >
          {codeGroups.map((group) => (
            <SelectorOption
              key={group.name}
              active={group.name === selectedCodeName}
              onSelect={() => onSelectCodeGroup(group)}
            >
              {group.name}
            </SelectorOption>
          ))}
        </SelectorPanel>

        <SelectorPanel title="Price" empty={!activeGroup}>
          {activeGroup?.codes.map((code) => (
            <SelectorOption
              key={code.CodeNo}
              active={Number(code.CodeNo) === Number(selectedCodeNo)}
              disabledReason={getCodeDisabledReason(code)}
              onSelect={() => setSelectedCodeNo(Number(code.CodeNo))}
            >
              {formatPrice(code.Price)} / {formatTerm(code.Term)}
            </SelectorOption>
          ))}
        </SelectorPanel>
      </div>

      <div className="mt-3 px-3 py-2 rounded-[4px] bg-[#f5f5f5] border border-[#e9e9e9] text-[14px] text-[#50565e] min-h-[38px] flex items-center">
        {selectedCode ? (
          <span>
            <span className="mr-2">Selected:</span>
            <span className="font-medium text-black">
              {[
                selectedItemLabel,
                selectedCodeName,
                `${formatPrice(selectedCode.Price)} / ${formatTerm(selectedCode.Term)}`,
              ].join(" - ")}
            </span>
          </span>
        ) : (
          <span className="text-gray-400">Select an item, code and price.</span>
        )}
      </div>

      <div className="flex justify-center mt-4 gap-4">
        <Button
          type="primary"
          className="mr-2 w-[104px] h-[30px] font-medium"
          loading={saving}
          disabled={
            saving ||
            !selectedCode ||
            Boolean(getCodeDisabledReason(selectedCode))
          }
          onClick={onApply}
        >
          <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
          Apply
        </Button>
        <Button
          onClick={onCancel}
          disabled={saving}
          className="reset-button w-[104px] h-[30px] font-medium"
        >
          <CloseOutlined className="font-medium" />
          Close
        </Button>
      </div>

      <Modal
        open={codeModalOpen}
        closable={false}
        onCancel={() => setCodeModalOpen(false)}
        footer={null}
        width={580}
        centered
        destroyOnClose
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <FaAddressCard className="text-[15px] text-black ml-1" />
            <span className="text-[14px] font-[Poppins] text-black ml-2">
              Add Code
            </span>
          </div>
        }
      >
        <LicenseCodeForm
          isModalForm
          defaultValues={{
            Item: selectedItem,
            ...(isOemLicense && hasValue(oemNo) ? { OemNo: oemNo } : {}),
          }}
          onCancel={() => setCodeModalOpen(false)}
          onCreated={handleCodeCreated}
        />
      </Modal>
    </>
  );
};
